import {
  type Entry,
  type Horizon,
  type HorizonItem,
  addIntent,
  createEntry,
  listDay,
  listInbox,
  listPlan,
  rollCounts,
  shift,
  today,
  toggleIntent,
  updateEntry,
} from "@beresta/core";
import { Icon, MoodScale, Note, Pressable, Section, Text } from "@beresta/ui";
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { useCompose } from "../compose/Compose.js";
import { useDb } from "../db/provider.js";
import { bandTitle, weekdayLabel } from "../horizonLabel.js";
import { dbFailedText } from "../db/failed.js";
import { t } from "../i18n.js";
import { useRouter } from "../router/index.js";
import { useSyncRevision } from "../sync/revision.js";
import { rememberPlanWritten } from "./firstPlan.js";
import { PlanList } from "./PlanList.js";

// Уривок запису — розбір CRDT, тобто Yjs. У холодному старті йому не місце.
const DayEntries = lazy(() => import("./DayEntries.js"));
const RollOverDialog = lazy(() => import("./RollOverDialog.js"));
const PlanSheet = lazy(() => import("./PlanSheet.js"));

/**
 * Заголовок блоку сусіднього дня. Поруч із сьогодні — «Учора», «Сьогодні»,
 * «Завтра»; далі — «Попередній» чи «Наступний день», бо дата вже стоїть
 * другим рядком і повторювати її не треба.
 */
function hopLabel(day: Horizon, side: "prev" | "next"): string {
  const now = today().toString();
  if (day.key === now) return t("nav.today");
  if (day.key === shift({ kind: "day", key: now }, -1).key) return t("time.yesterday");
  if (day.key === shift({ kind: "day", key: now }, 1).key) return t("time.tomorrow");
  return side === "prev" ? t("day.hopPrev") : t("day.hopNext");
}

/**
 * T4 — день (картки ScreenHome і ScreenDay). Порядок: черга інбокса, настрій,
 * план, записи, сусідні дні — і він не випадковий. Настрій — одна дія на
 * дотик, яку роблять мимохідь. План — те, по що приходять зранку. Записи —
 * увечері, і ростуть донизу.
 *
 * Сьогоднішній день відрізняється трьома речами: позначка «Сьогодні» в шапці,
 * черга інбокса вгорі й блоки «Учора» та «Завтра» внизу.
 */
export function DayScreen({ horizon }: { readonly horizon: Horizon }) {
  const db = useDb();
  if (db.status === "starting") return <Note tone="plain">{t("db.starting")}</Note>;
  if (db.status === "failed") return <Note tone="alert">{dbFailedText(db.reason)}</Note>;
  return <DayBody horizon={horizon} />;
}

function DayBody({ horizon }: { readonly horizon: Horizon }) {
  const db = useDb();
  const ctx = db.status === "ready" ? db.ctx : null;
  const { go } = useRouter();
  const compose = useCompose();
  const isToday = horizon.key === today().toString();

  const [items, setItems] = useState<readonly HorizonItem[]>([]);
  const [counts, setCounts] = useState<ReadonlyMap<string, number>>(new Map());
  const [moodEntry, setMoodEntry] = useState<Entry | null>(null);
  const [inbox, setInbox] = useState(0);
  const [rolling, setRolling] = useState(false);
  const [editing, setEditing] = useState<HorizonItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const moodChain = useRef<Promise<Entry | null>>(Promise.resolve(null));

  const reload = useCallback(async () => {
    if (!ctx) return;
    try {
      const loaded = await listPlan(ctx, horizon.key);
      setItems(loaded);
      setCounts(await rollCounts(ctx, loaded.filter((i) => i.rolledFromId !== null).map((i) => i.id)));
      if (loaded.length > 0) rememberPlanWritten();
      // Настрій належить дню (E9); живе він на першому щоденниковому записі дня.
      const entries = await listDay(ctx, horizon.key);
      const carrier = entries.find((e) => e.kind === "journal") ?? null;
      setMoodEntry(carrier);
      moodChain.current = Promise.resolve(carrier);
      if (isToday) setInbox((await listInbox(ctx)).length);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [ctx, horizon.key, isToday]);

  const revision = useSyncRevision();
  useEffect(() => {
    void reload();
  }, [reload, revision, compose.revision]);

  const add = useCallback(
    async (text: string) => {
      if (!ctx) return;
      try {
        await addIntent(ctx, horizon.key, text);
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [ctx, horizon.key, reload],
  );

  const toggle = useCallback(
    async (id: string) => {
      if (!ctx) return;
      try {
        await toggleIntent(ctx, id);
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [ctx, reload],
  );

  /**
   * Вибір настрою. Ланцюгом, як і збереження запису: два швидкі дотики на
   * порожньому дні інакше створили б два записи-носії.
   */
  const pickMood = useCallback(
    (mood: number) => {
      if (!ctx) return;
      const next = moodChain.current.then(async (current) => {
        try {
          const saved = current
            ? await updateEntry(ctx, current.id, { mood: mood === 0 ? null : mood })
            : mood === 0
              ? null
              : await createEntry(ctx, { kind: "journal", entryDate: horizon.key, mood });
          setMoodEntry(saved);
          return saved;
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e));
          return current;
        }
      });
      moodChain.current = next;
      setMoodEntry((was) => (was ? { ...was, mood: mood === 0 ? null : mood } : was));
    },
    [ctx, horizon.key],
  );

  const undone = items.filter((i) => i.doneAt === null);
  const past = horizon.key < today().toString();
  const yesterday = shift(horizon, -1);
  const tomorrow = shift(horizon, 1);

  return (
    <>
      {isToday && inbox > 0 && (
        <button type="button" className="block lrow inbox-row" onClick={() => go({ kind: "page", page: "inbox" })}>
          <span className="lrow-main body-ui">{t("day.inbox")}</span>
          <span className="cnt caption-1">{inbox}</span>
          <Icon name="forward" />
        </button>
      )}

      <Section title={t("day.mood")}>
        <div className="block pad">
          <MoodScale label={t("day.mood")} value={moodEntry?.mood ?? 0} onChange={pickMood} />
        </div>
      </Section>

      {/* `id` тут не оздоба: він дає секції доступну назву через
          `aria-labelledby` і водночас служить гаком для перевірок —
          прив’язка до видимого тексту вже одного разу зламала нічний
          перф-прогін, коли напис став «План дня». */}
      <Section id="day-plan" title={t("day.plan")}>
        <PlanList
          items={items}
          onToggle={(id) => void toggle(id)}
          rollCounts={counts}
          onAdd={(text) => void add(text)}
          onOpen={setEditing}
        />
        {past && undone.length > 0 && (
          <Pressable variant="quiet" onClick={() => setRolling(true)}>
            {t("plan.rollAll")}
          </Pressable>
        )}
      </Section>

      <Section title={t("day.entries")}>
        <Suspense fallback={null}>
          <DayEntries horizon={horizon} isToday={isToday} revision={revision + compose.revision} />
        </Suspense>
      </Section>

      {error !== null && <Note tone="alert" role="alert">{error}</Note>}

      {/*
        Сусідні дні — на кожному дні, а не лише на сьогоднішньому: інакше
        один крок уперед лишав людину без шляху до наступного.
      */}
      <nav className="hop" aria-label={t("nav.hop")}>
        <button type="button" className="block" onClick={() => go({ kind: "horizon", horizon: yesterday })}>
          <span className="hop-t headline">
            <Icon name="back" />
            {hopLabel(yesterday, "prev")}
          </span>
          <Text style="footnote" tone="ink3">
            {t("day.hopDate", { weekday: weekdayLabel(yesterday), date: bandTitle(yesterday) })}
          </Text>
        </button>
        <button type="button" className="block next" onClick={() => go({ kind: "horizon", horizon: tomorrow })}>
          <span className="hop-t headline">
            {hopLabel(tomorrow, "next")}
            <Icon name="forward" />
          </span>
          <Text style="footnote" tone="ink3">
            {t("day.hopDate", { weekday: weekdayLabel(tomorrow), date: bandTitle(tomorrow) })}
          </Text>
        </button>
      </nav>

      {rolling && (
        <Suspense fallback={null}>
          <RollOverDialog horizon={horizon} items={undone} onClose={() => setRolling(false)} onMoved={() => void reload()} />
        </Suspense>
      )}
      {editing && (
        <Suspense fallback={null}>
          <PlanSheet item={editing} horizon={horizon} onClose={() => setEditing(null)} onChanged={() => void reload()} />
        </Suspense>
      )}
    </>
  );
}
